import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  STRONG_DROPDOWN_PAPER_SX,
  strongSelectMenuProps,
} from "components/common/dropdownPaper";
import {
  addDays,
  addWeeks,
  endOfDay,
  endOfWeek,
  format,
  startOfDay,
  startOfWeek,
  subDays,
  subWeeks,
} from "date-fns";
import { useEffect, useRef, useState } from "react";

/** Shared dimensions for every filter cell (date range, search, selects). */
export const FILTER_FIELD_W = 220;
export const FILTER_FIELD_H = 40;

/**
 * Drop focus from the control that opened a menu, so a single click anywhere
 * else fully deselects the filter (no lingering focus ring eating the next
 * click). Deferred so MUI finishes its close/focus-restore first.
 */
export const blurActiveElement = () =>
  setTimeout(() => {
    (document.activeElement as HTMLElement | null)?.blur?.();
  }, 0);

export interface RangeValue {
  after?: string;
  before?: string;
}

const CUSTOM = "__custom__";

export const ALL_PRESETS = [
  { value: "last_week", label: "Last Week" },
  { value: "this_week", label: "This Week" },
  { value: "yesterday", label: "Yesterday" },
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "next_week", label: "Next Week" },
] as const;

/** Past-only presets (drop future options) for "happened already" fields. */
export const PAST_PRESETS = ALL_PRESETS.filter(
  (p) => p.value !== "tomorrow" && p.value !== "next_week",
);

const toIsoRange = (from: Date, to: Date): RangeValue => ({
  after: startOfDay(from).toISOString(),
  before: endOfDay(to).toISOString(),
});

export function presetToRange(value: string): RangeValue {
  const now = new Date();
  switch (value) {
    case "today":
      return toIsoRange(now, now);
    case "yesterday": {
      const d = subDays(now, 1);
      return toIsoRange(d, d);
    }
    case "tomorrow": {
      const d = addDays(now, 1);
      return toIsoRange(d, d);
    }
    case "this_week":
      return toIsoRange(startOfWeek(now), endOfWeek(now));
    case "last_week": {
      const d = subWeeks(now, 1);
      return toIsoRange(startOfWeek(d), endOfWeek(d));
    }
    case "next_week": {
      const d = addWeeks(now, 1);
      return toIsoRange(startOfWeek(d), endOfWeek(d));
    }
    default:
      return {};
  }
}

/** Range label for the cell — shows both years (e.g. 12/15/25 - 12/22/26). */
export function customRangeLabel(after?: string, before?: string): string {
  const f = (iso: string) => format(new Date(iso), "MM/dd/yy");
  if (after && before) return `${f(after)} - ${f(before)}`;
  if (after) return `${f(after)} - …`;
  if (before) return `… - ${f(before)}`;
  return "";
}

/**
 * Outlined select cell with a floating label, relative presets and a custom
 * date-range popover. Shared by the Integrations list and the History/Steps
 * filter bars. The label only floats when a value is set (shrink/notched keyed
 * to ``hasValue``) so it never hangs after the menu is dismissed.
 */
export default function DateRangeSelect({
  label,
  value,
  onChange,
  presets = ALL_PRESETS,
}: {
  label: string;
  value: RangeValue;
  onChange: (range: RangeValue) => void;
  presets?: readonly { value: string; label: string }[];
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedKey, setSelectedKey] = useState("");
  const [customOpen, setCustomOpen] = useState(false);
  const [tempStart, setTempStart] = useState<Date | null>(null);
  const [tempEnd, setTempEnd] = useState<Date | null>(null);

  const hasValue = Boolean(value.after || value.before);

  useEffect(() => {
    if (!hasValue) setSelectedKey("");
  }, [hasValue]);

  const openCustom = () => {
    setTempStart(value.after ? new Date(value.after) : null);
    setTempEnd(value.before ? new Date(value.before) : null);
    setCustomOpen(true);
  };

  const handleSelect = (e: SelectChangeEvent) => {
    const key = e.target.value;
    if (key === CUSTOM) return; // handled by the MenuItem's onClick
    setSelectedKey(key);
    onChange(presetToRange(key));
  };

  const applyCustom = () => {
    setSelectedKey(CUSTOM);
    onChange({
      after: tempStart ? startOfDay(tempStart).toISOString() : undefined,
      before: tempEnd ? endOfDay(tempEnd).toISOString() : undefined,
    });
    setCustomOpen(false);
  };

  const clearCustom = () => {
    setTempStart(null);
    setTempEnd(null);
    setSelectedKey("");
    onChange({ after: undefined, before: undefined });
    setCustomOpen(false);
  };

  const renderValue = (key: string) =>
    key === CUSTOM
      ? customRangeLabel(value.after, value.before)
      : (ALL_PRESETS.find((p) => p.value === key)?.label ?? "");

  const labelId = `daterange-${label.replace(/\s+/g, "-").toLowerCase()}`;

  return (
    <FormControl size="small" sx={{ width: FILTER_FIELD_W }} ref={anchorRef}>
      <InputLabel id={labelId} shrink={hasValue}>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        label={label}
        notched={hasValue}
        value={selectedKey}
        renderValue={renderValue}
        onChange={handleSelect}
        onClose={blurActiveElement}
        sx={{ height: FILTER_FIELD_H }}
        MenuProps={strongSelectMenuProps}
      >
        <MenuItem value="">Anytime</MenuItem>
        {presets.map((p) => (
          <MenuItem key={p.value} value={p.value}>
            {p.label}
          </MenuItem>
        ))}
        <MenuItem value={CUSTOM} onClick={openCustom}>
          Select Period
        </MenuItem>
      </Select>

      <Popover
        open={customOpen}
        anchorEl={anchorRef.current}
        onClose={() => {
          setCustomOpen(false);
          blurActiveElement();
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ elevation: 0, sx: STRONG_DROPDOWN_PAPER_SX }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={{
              p: 2,
              width: 240,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <DatePicker
              label="From"
              value={tempStart}
              onChange={setTempStart}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
            <DatePicker
              label="To"
              value={tempEnd}
              onChange={setTempEnd}
              minDate={tempStart ?? undefined}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button size="small" color="inherit" onClick={clearCustom}>
                Clear
              </Button>
              <Button size="small" variant="contained" onClick={applyCustom}>
                Apply
              </Button>
            </Box>
          </Box>
        </LocalizationProvider>
      </Popover>
    </FormControl>
  );
}
